import { forwardRef, useState, useEffect, Ref, useMemo } from 'react';
import { cx } from '@linaria/atomic';
import composeRefs from '@seznam/compose-react-refs';
import { Icon, IconName } from '@frontend/icons';
import { KeyNames } from '../../constants/key-names';
import { getInitials } from '../../helpers/get-initials';
import { getFirstLastInitials } from '../../helpers/get-initials';
import type { StylesProp } from '../../helpers/styles-prop';
import { useImageReady } from '../../hooks/animations/use-image-ready';
import { useTooltip } from '../tooltip/use-tooltip';
import { BottomIcon } from './atoms/bottom-icon';
import { AvatarDot } from './atoms/dot';
import {
  AI_ASSISTANT_STYLES,
  getContainerClassName,
  CONTENT_STYLES,
  type SizeProps as AvatarSize,
  sizeStyles,
} from './avatar.styles';
import { getColor } from './helpers';

export interface AvatarProps {
  alt?: string;
  className?: string;
  disableClick?: boolean;
  firstName?: string;
  lastName?: string;
  id?: string;
  /**
   * @deprecated Use `iconName="weave-logo"` instead.
   */
  isWeave?: boolean;
  /**
   * @deprecated Use `iconName="user"` instead.
   */
  isUser?: boolean;
  isAiAssistant?: boolean;
  iconName?: IconName;
  userId?: string;
  name?: string;
  onClick?: (e?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
  size?: AvatarSize;
  src?: string;
  style?: React.CSSProperties;
  title?: string;
  children?: React.ReactNode;
  bypassColor?: { fill: string; text: string };
  css?: StylesProp;
  disableHoverLabel?: boolean;
}

const smallerThanLargeVariants: AvatarSize[] = ['medium', 'small', 'xs'];

const IconAvatar: React.FC<{ name: IconName; size: AvatarSize }> = ({ name, size }) => (
  <Icon name={name} size={smallerThanLargeVariants.includes(size) ? 16 : 24} />
);

const InitialsAvatar: React.FC<{
  firstName?: string;
  lastName?: string;
  name?: string;
  color: { fill: string; text: string };
}> = ({ firstName, lastName, name, color }) => (
  <figcaption style={{ backgroundColor: color.fill, color: color.text }}>
    {!!firstName && !!lastName ? getFirstLastInitials(firstName, lastName) : getInitials(name || '')}
  </figcaption>
);

const AiAssistantAvatar: React.FC = () => <Icon name='aiweave-assistant' size={24} />;

const ImageAvatar: React.FC<{ src: string; name: string; onError: () => void }> = ({ src, name, onError }) => {
  const imgProps = useImageReady();

  return <img {...imgProps} onError={onError} src={src} alt={`${name || 'Unnamed'} avatar`} />;
};

const AvatarOptional = forwardRef<HTMLElement, AvatarProps>(
  (
    {
      alt,
      className,
      disableClick,
      id,
      userId,
      isWeave,
      isUser = false,
      isAiAssistant = false,
      iconName,
      name,
      firstName,
      lastName,
      onClick,
      size = 'medium',
      src,
      title,
      bypassColor,
      children,
      disableHoverLabel,
      css: cssProp,
      ...rest
    },
    ref
  ) => {
    const derivedName = firstName && lastName ? `${firstName} ${lastName}` : name;

    const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'bottom', hoverDelay: 300 });

    const { ref: tooltipTriggerRef, ...restTriggerProps } = triggerProps;

    const [isError, setIsError] = useState(false);

    const isInteractive = !!onClick && !disableClick;

    const normalizedSrc = !isError ? src : '';

    const toolTipValue = title || derivedName;

    useEffect(() => {
      if (isError) {
        setIsError(false);
      }
    }, [src]);

    const identifier = userId || derivedName || 'default';

    const color = useMemo(() => bypassColor || getColor(identifier, isUser), [bypassColor, identifier, isUser]);

    const renderAvatarContent = () => {
      if (isAiAssistant) return <AiAssistantAvatar />;
      if (iconName) return <IconAvatar name={iconName} size={size} />;
      if (isWeave) return <IconAvatar name='weave-logo' size={size} />;
      if (!normalizedSrc && firstName && lastName)
        return <InitialsAvatar firstName={firstName} lastName={lastName} color={color!} />;
      if (!normalizedSrc && derivedName) return <InitialsAvatar name={derivedName} color={color!} />;
      if (!normalizedSrc && !derivedName) return <IconAvatar name='user' size={size} />;
      return <ImageAvatar src={normalizedSrc ?? ''} name={derivedName || ''} onError={() => setIsError(true)} />;
    };

    const handleClick = (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      if (disableClick) {
        return;
      } else onClick?.(e);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === KeyNames.Enter || event.key === KeyNames.Space) {
        handleClick(event);
      }
    };

    return (
      <>
        <figure
          ref={composeRefs(ref, tooltipTriggerRef as Ref<HTMLElement>)}
          aria-label={`${derivedName || 'Unnamed'} avatar`}
          className={cx(getContainerClassName({ size, isInteractive }), sizeStyles[size], className)}
          css={cssProp}
          id='avatar'
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          tabIndex={isInteractive ? 0 : -1}
          {...(disableHoverLabel ? undefined : restTriggerProps)}
          {...rest}
        >
          <div
            className={cx(CONTENT_STYLES, isAiAssistant && AI_ASSISTANT_STYLES)}
            style={!normalizedSrc && derivedName ? { backgroundColor: color!.fill, color: color!.text } : undefined}
          >
            {renderAvatarContent()}
          </div>

          {children}
        </figure>

        {!disableHoverLabel && toolTipValue && <Tooltip {...tooltipProps}>{toolTipValue}</Tooltip>}
      </>
    );
  }
);

AvatarOptional.displayName = 'Avatar';

const AvatarNamespace = Object.assign(AvatarOptional, {
  Dot: AvatarDot,
  BottomIcon: BottomIcon,
});

export { AvatarNamespace as Avatar };
